import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { usePayInvoiceMutation } from 'entities/Invoice/api/payInvoiceApi';
import { PaymentProcessor, PaymentMethod, PaymentMethodSetting } from 'entities/Payment';
import { Button } from 'shared/ui/Button';
import { CollapseRadioButtonGroup } from 'shared/ui/CollapseRadioButtonGroup';
import { Checkbox } from 'shared/ui/Checkbox';
import { Modal } from 'shared/ui/Modal';
import { showNotification } from 'app/providers/NotificationsProvider';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useNavigate } from 'react-router-dom';
import { Invoice, useBalancePay } from 'entities/Invoice';
import { AutochargeStatus } from 'entities/Contract';
import { InfoTag } from 'shared/ui/InfoTag';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { ProfileVerificationStatus, useGetUserByIdQuery } from 'entities/User';
import { UploadDocumentsModal } from 'widgets/AccountDetailsForm/ui/UploadDocumentsModal';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface PaymentModalProps {
  invoice: Invoice;
  isOpen: boolean;
  onClose: () => void;
}

export const PaymentModal: FC<PaymentModalProps> = memo((props) => {
  const { invoice, isOpen, onClose } = props;

  const navigate = useNavigate();

  const { t } = useAppTranslation(['booking', 'common']);

  const currencySymbol = useGetCurrencySymbol();
  const { data: user } = useGetUserByIdQuery({ userId: invoice?.user.userId });

  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.ONLINE_PAYMENT);
  const [isAutoPaymentEnabled, setAutoPaymentEnabled] = useState(false);
  const [useUserBalance, setUseUserBalance] = useState(false);
  const [areDocsAreUnderReviewProcess, setAreDocsUnderReviewProcessModalOpened] = useState(false);

  const [isUploadDocsModalOpened, setUploadDocsModalOpened] = useState(false);

  const verificationStatus = user?.profileVerificationStatus || null;

  const { isBalancePayAvaliable, amountToCharge } = useBalancePay(invoice);
  const globalSettings = useAppSelector(getGlobalSettings);
  const [payInvoice] = usePayInvoiceMutation();

  const isAutochargesAllowed = useMemo(
    () => invoice?.contract?.autocharges === AutochargeStatus.DISABLED,
    [invoice?.contract?.autocharges],
  );

  const amountToPay = useMemo(
    () => (useUserBalance ? roundNumber(invoice.debt - amountToCharge) : invoice.debt),
    [amountToCharge, invoice.debt, useUserBalance],
  );

  useEffect(() => {
    const watchAutoPaymentStatus = (): void => {
      switch (paymentMethod) {
        case PaymentMethod.ONLINE_PAYMENT:
          setAutoPaymentEnabled(false);
          break;
        case PaymentMethod.BANK_TRANSFER:
          setAutoPaymentEnabled(false);
          break;
        case PaymentMethod.OTHER:
          setAutoPaymentEnabled(false);
          break;
      }
    };

    watchAutoPaymentStatus();
  }, [paymentMethod]);

  const toggleAutoPayment = useCallback((): void => {
    setAutoPaymentEnabled((prevState) => !prevState);
  }, []);

  const toggleUseUserBalance = useCallback((): void => {
    setUseUserBalance((prevState) => !prevState);
  }, []);

  const handlePayInvoice = useCallback(async (): Promise<void | undefined> => {
    if (verificationStatus === ProfileVerificationStatus.PENDING) {
      setAreDocsUnderReviewProcessModalOpened(true);
      return;
    }

    if (verificationStatus === null || [ProfileVerificationStatus.REJECTED].includes(verificationStatus)) {
      setUploadDocsModalOpened(true);
    } else {
      if (invoice) {
        try {
          const response = await payInvoice({
            invoiceId: invoice.invoiceId,
            paymentMethod,
            isAutopayment: isAutoPaymentEnabled,
            useUserBalance,
          }).unwrap();

          if (response.redirectionUrl) {
            if (response?.redirectionUrl?.includes(PaymentProcessor.STRIPE || PaymentProcessor.SEPA)) {
              const query = response?.redirectionUrl.split('?')[1];
              navigate(`${AppRoutes.CHECKOUT}?${query}`);
            } else {
              window.location.href = response.redirectionUrl;
            }
          } else {
            showNotification('info', t('Success'), t('Invoice has been successfully paid'));
          }
        } catch (error: CustomAny) {
          console.log(error);
          showNotification('error', t('Error'), t('Error when paying invoice'));
        } finally {
          onClose();
        }
      }
    }
  }, [invoice, payInvoice, paymentMethod, isAutoPaymentEnabled, useUserBalance, navigate, t, onClose, verificationStatus]);

  const onlinePaymentMethodSetting = globalSettings?.publicPaymentMethods.find(
    (paymentMethodSetting: PaymentMethodSetting) => paymentMethodSetting.name /* === PaymentMethod.ONLINE_PAYMENT */,
  );

  const paymentMethodsOptions = [
    {
      label: (
        <div className="flex flex-col items-start justify-between w-full desktop:flex-row desktop:items-center">
          <span>{t('Online payment')}</span>
          <div className="flex flex-wrap items-center gap-2">
            {onlinePaymentMethodSetting?.imageUrl && <img src={onlinePaymentMethodSetting.imageUrl} className="max-h-[100px]" />}
          </div>
        </div>
      ),
      value: PaymentMethod.ONLINE_PAYMENT,
      content:
        isAutochargesAllowed && onlinePaymentMethodSetting?.isAutoPaymentAllowed ? (
          <div>
            <>
              <div className="font-semibold text-lg">{t('Auto pay')}</div>
              <div className="mb-2">
                {t('Turn on the automatic payment feature so you don`t miss payments. You can turn it off at any time!')}
              </div>
              <Checkbox
                name="isAutopayment"
                checked={isAutoPaymentEnabled}
                onChange={toggleAutoPayment} /* disabled={isPrePaymentApplied} */
              >
                {t('Allow autopayment')}
              </Checkbox>
            </>
          </div>
        ) : null,
    },
  ];

  const actions = useCallback((): JSX.Element => {
    return (
      <div className="flex space-x-3 justify-end">
        <Button theme="secondary" onClick={onClose}>
          {t('Cancel', { ns: 'common' })}
        </Button>
        <Button onClick={handlePayInvoice}>
          {amountToPay > 0
            ? t('Pay {{amountToPay}} {{currencySymbol}}', { ns: 'common', amountToPay: formatPrice(amountToPay), currencySymbol })
            : t('Continue')}
        </Button>
      </div>
    );
  }, [onClose, t, handlePayInvoice, amountToPay, currencySymbol]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} footer={actions()}>
        <div className="min-h-[250px]">
          <div className="font-semibold text-3xl mb-6">{t('Select payment method')}</div>
          {isBalancePayAvaliable && (
            <div className="mt-2 mb-6">
              <InfoTag textSize="medium">
                {t(
                  'You have {{balance}} {{currencySymbol}} on your balance, for this Invoice you can charge {{amountToCharge}} {{currencySymbol}} from your balance',
                  {
                    balance: user?.balance,
                    currencySymbol,
                    amountToCharge: formatPrice(amountToCharge),
                  },
                )}
              </InfoTag>
              <Checkbox className="mt-3" name="useUserBalance" checked={useUserBalance} onChange={toggleUseUserBalance}>
                <span className="text-m">
                  {t('Use {{amountToCharge}} {{currencySymbol}} from balance', {
                    amountToCharge: formatPrice(amountToCharge),
                    currencySymbol,
                  })}
                </span>
              </Checkbox>
            </div>
          )}
          <CollapseRadioButtonGroup options={paymentMethodsOptions} selectedOption={paymentMethod} setSelectedOption={setPaymentMethod} />
        </div>
      </Modal>

      {user?.userId && (
        <UploadDocumentsModal
          isOpen={isUploadDocsModalOpened}
          onClose={() => {
            setUploadDocsModalOpened(false);
          }}
          accountType={user.accountType}
          userId={user.userId}
        />
      )}

      {areDocsAreUnderReviewProcess && (
        <Modal
          isOpen={areDocsAreUnderReviewProcess}
          onClose={() => {
            setAreDocsUnderReviewProcessModalOpened(false);
          }}
        >
          <div>
            <div className="font-semibold text-3xl mb-6 mt-6">{t('Sorry you cant perform payment.', { ns: 'common' })}</div>
            <div className="mt-2 mb-6 text-xl">
              {t(
                'We are reviewing your documents. You will be able to make a payment once your documents are approved. We will notify you.',
                { ns: 'common' },
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
});
